'use client'

import useWindowSize from '@/hooks/use-window-size'
import { logger } from '@/lib/logger'
import { usePaymentContext, usePaymentEventLogger } from '@/lib/telemetry/payment-hooks'
import { cFormatter } from '@/lib/utils'
import { useSignals } from '@preact/signals-react/runtime'
import { Info } from 'lucide-react'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Cart, CartTotals } from '../../_lib/commerce'
import { cartTotals } from '../../_lib/signals/checkout'

type PaymentDetailsPanelProps = {
  cart: Cart
}
const CheckoutDetailsPanel = ({ cart }: PaymentDetailsPanelProps) => {
  const { isMobile } = useWindowSize()
  const [isCollapsed, setIsCollapsed] = useState(false)
  useSignals()

  // Initialize payment telemetry
  const { context } = usePaymentContext({
    processor: 'commerce',
    method: 'credit_card',
    merchantId: cart.merchant?.merchantProfileId?.toString() || cart.merchant?.tenantId?.toString(),
    amount: cartTotals.value.total,
    currency: 'USD',
    sessionId:
      typeof window !== 'undefined' ? window.sessionStorage.getItem('checkout_session_id') || undefined : undefined,
  })

  const { logEvent } = usePaymentEventLogger(context)

  useEffect(() => {
    setIsCollapsed(isMobile)
  }, [isMobile])

  // Log checkout panel view
  useEffect(() => {
    if (cart && cartTotals.value.total > 0) {
      logEvent('payment.checkout.viewed', undefined, {
        'checkout.cart_items': cart.items?.length || 0,
        'checkout.subtotal': cart.subtotal || 0,
        'checkout.tax': cart.tax || 0,
        'checkout.fees': cartTotals.value.fees,
        'checkout.total': cartTotals.value.total,
        'checkout.merchant_pays_fees': cartTotals.value.merchantPays,
        'checkout.is_mobile': isMobile,
      })

      // Store payment session for abandonment tracking
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(
          'payk12_payment_in_progress',
          JSON.stringify({
            correlationId: context.correlationId,
            processor: context.processor,
            method: context.method,
            amount: cartTotals.value.total,
            timestamp: Date.now(),
          }),
        )
      }
    }
  }, [cart, cartTotals.value, logEvent, context, isMobile])
  return (
    <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
      <h2 id="summary-heading" className="sr-only">
        Order summary
      </h2>
      <div className="flex flex-row items-center">
        {isMobile && (
          <div className="flex-1 justify-center text-3xl font-bold">
            Checkout{' '}
            <Info
              className="ml-2 inline-block cursor-pointer text-lg text-gray-400"
              onClick={() => setIsCollapsed(!isCollapsed)}
            />
          </div>
        )}
        <dl className="flex-1 pb-2 text-right">
          <dt className="text-sm font-medium">Amount due</dt>
          <dd className="mt-1 text-3xl font-bold tracking-tight text-white">{cFormatter(cartTotals.value.total)}</dd>
        </dl>
      </div>
      {!isCollapsed && (
        <>
          <ul
            role="list"
            className={twMerge('divide-y divide-white divide-opacity-10 text-sm font-medium', isMobile ? 'hidden' : '')}
          >
            {(cart?.items || [])?.map((product) => (
              <li key={product.id} className="flex items-start space-x-4 py-6">
                <img
                  src={product.image ?? cart.merchant?.logo}
                  alt={'product'}
                  className="h-20 w-20 flex-none rounded-md border-4 border-white bg-white object-contain object-center"
                />
                <div className="flex-auto space-y-1">
                  <h3 className="text-white">{product.name}</h3>
                  <p>Qty: {product.qty}</p>
                  <p>{product.description?.replace(/(<([^>]+)>)/gi, '').replace(/&nbsp;|amp;/gi, ' ')}</p>
                </div>
                <p className="flex-none text-base font-medium text-white">{cFormatter(product.itemAmount)}</p>
              </li>
            ))}
          </ul>

          <dl className="space-y-6 border-t border-white border-opacity-10 pt-6 text-sm font-medium">
            <div className="flex items-center justify-between">
              <dt>Subtotal</dt>
              <dd>{cFormatter(cart?.subtotal || 0)}</dd>
            </div>

            <div className="flex items-center justify-between">
              <dt>Taxes</dt>
              <dd>{cFormatter(cart?.tax || 0)}</dd>
            </div>
            {!cartTotals.value.merchantPays && cartTotals.value.fees > 0 && (
              <div className="flex items-center justify-between">
                <dt>Fees</dt>
                <dd>{cFormatter(cartTotals.value.fees)}</dd>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-white border-opacity-10 pt-6 text-white">
              <dt className="text-base">Total</dt>
              <dd className="text-base">{cFormatter(cartTotals.value.total)}</dd>
            </div>
          </dl>
          {isMobile && (
            <div className="flex-1 text-center" onClick={() => setIsCollapsed(!isCollapsed)}>
              close
            </div>
          )}
        </>
      )}
    </div>
  )
}
export default CheckoutDetailsPanel
